import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import {
    Box,
    Button,
    CssBaseline,
    FormLabel,
    FormControl,
    TextField,
    Typography,
    CircularProgress,
    Snackbar,
    Alert
} from '@mui/material';

import MuiCard from '@mui/material/Card';
import { styled } from '@mui/material/styles';

import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import AppTheme from '../../shared-theme/AppTheme';

import loginBg from '../../assets/dummies/login-bg.jpg';
import Config from "../../config/Config";

const Card = styled(MuiCard)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'center',
    width: '100%',
    padding: theme.spacing(4),
    gap: theme.spacing(2),
    margin: 'auto',
    [theme.breakpoints.up('sm')]: {
        maxWidth: '450px',
    },
    boxShadow:
        'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
    ...theme.applyStyles('dark', {
        boxShadow:
            'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
    }),
}));

export default function SignIn(props) {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [emailError, setEmailError] = useState(false);
    const [emailErrorMessage, setEmailErrorMessage] = useState('');
    const [passwordError, setPasswordError] = useState(false);
    const [passwordErrorMessage, setPasswordErrorMessage] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [fadeOut, setFadeOut] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastSeverity, setToastSeverity] = useState('error');

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            navigate('/splash');
        } else {
            const timer = setTimeout(() => {
                localStorage.clear();
                sessionStorage.clear();

                setFadeOut(true);
                setTimeout(() => setIsLoading(false), 600);
            }, 2000);

            return () => clearTimeout(timer);
        }
    }, [navigate]);

    const handleTogglePassword = () => {
        setShowPassword((prev) => !prev);
    };

    const validateInputs = () => {
        let isValid = true;

        if (!email || !/\S+@\S+\.\S+/.test(email)) {
            setEmailError(true);
            setEmailErrorMessage('Please enter a valid email address.');
            isValid = false;
        } else {
            setEmailError(false);
            setEmailErrorMessage('');
        }

        if (!password || password.length < 6) {
            setPasswordError(true);
            setPasswordErrorMessage('Password must be at least 6 characters long.');
            isValid = false;
        } else {
            setPasswordError(false);
            setPasswordErrorMessage('');
        }

        return isValid;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!validateInputs()) {
            return;
        }

        setIsLoggingIn(true);
        try {
            if (email === "admin@task.com") {
                const getTaskLogin = await axios.post(`${Config.localApi}fetch-admin`, { email: email, password: password });
                if (getTaskLogin.data.status === "success") {
                    // Store only JWT token - all user data comes from token verification
                    login(getTaskLogin.data.user, getTaskLogin.data.token);
                    setToastMessage('Login successful!');
                    setToastSeverity('success');
                    setOpenSnackbar(true);
                    navigate('/splash', { replace: true });
                } else {
                    setToastMessage(getTaskLogin.data.message || 'Login failed.');
                    setToastSeverity('error');
                    setOpenSnackbar(true);
                }
            } else {
                const response = await axios.post('https://hrm.nuroil.com/api/v1/task/login', {
                    email,
                    password
                });

                if (response.data.status === "success") {
                    const getTaskLogin = await axios.get(`${Config.localApi}fetch-logged-user/${response.data.data.user_id}`);

                    if (getTaskLogin.data.status === 'success') {
                        // Store only JWT token - all user data comes from token verification
                        login(getTaskLogin.data.user, getTaskLogin.data.token);
                        setToastMessage('Login successful!');
                        setToastSeverity('success');
                        setOpenSnackbar(true);
                        navigate('/splash');
                    } else {
                        setToastMessage(getTaskLogin.data.message || 'Login failed.');
                        setToastSeverity('error');
                        setOpenSnackbar(true);
                    }
                } else {
                    setToastMessage(response.data.message || 'Login failed.');
                    setToastSeverity('error');
                    setOpenSnackbar(true);
                }
            }
        } catch (error) {
            console.error("Login error:", error);
            if (error.response) {
                setToastMessage(error.response.data.message || 'An error occurred during login.');
            } else if (error.request) {
                setToastMessage('No response from server. Please check your internet connection.');
            } else {
                setToastMessage('An unexpected error occurred.');
            }
            setToastSeverity('error');
            setOpenSnackbar(true);
        } finally {
            setIsLoggingIn(false);
        }
    };

    if (isLoading) {
        return (
            <Box
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    backgroundColor: '#fefefe',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                    zIndex: 9999,
                    opacity: fadeOut ? 0 : 1,
                    transition: 'opacity 0.6s ease-in-out',
                }}
            >
                <Box
                    sx={{ width: 80, mb: 2 }}
                />
                <CircularProgress color="secondary" size={50} />
                <Typography variant="subtitle1" sx={{ mt: 2 }}>
                    Getting things ready...
                </Typography>
            </Box>
        );
    }

    return (
        <AppTheme {...props}>
            <CssBaseline />
            <Box sx={{ position: 'relative', width: '100vw', height: '100vh', overflow: 'hidden' }}>
                {/* Background image */}
                <Box
                    component="img"
                    src={loginBg}
                    alt="Login Background"
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        zIndex: 0,
                    }}
                />

                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1,
                        width: '100%',
                        maxWidth: '450px',
                        p: 2,
                    }}
                >
                    <Card
                        variant="outlined"
                        sx={{
                            backdropFilter: 'blur(10px)',
                            backgroundColor: 'rgba(255, 255, 255, 0.15)',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            borderRadius: 3,
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.25)',
                            padding: 4,
                        }}
                    >
                        <Typography
                            component="h1"
                            variant="h4"
                            sx={{
                                textAlign: 'center',
                                mb: 2,
                                fontWeight: 'bold',
                            }}
                        >
                            Welcome Back
                        </Typography>
                        <Box
                            component="form"
                            onSubmit={handleSubmit}
                            noValidate
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                width: '100%',
                                gap: 2,
                            }}
                        >
                            <FormControl>
                                <FormLabel htmlFor="email">Email</FormLabel>
                                <TextField
                                    error={emailError}
                                    helperText={emailErrorMessage}
                                    id="email"
                                    type="email"
                                    name="email"
                                    placeholder="your@email.com"
                                    autoComplete="email"
                                    autoFocus
                                    required
                                    fullWidth
                                    variant="outlined"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    color={emailError ? 'error' : 'primary'}
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel htmlFor="password">Password</FormLabel>
                                <TextField
                                    error={passwordError}
                                    helperText={passwordErrorMessage}
                                    name="password"
                                    placeholder="••••••"
                                    type={showPassword ? 'text' : 'password'}
                                    id="password"
                                    autoComplete="current-password"
                                    required
                                    fullWidth
                                    variant="outlined"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    color={passwordError ? 'error' : 'primary'}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={handleTogglePassword}
                                                    edge="end"
                                                    sx={{ border: 0 }}
                                                >
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </FormControl>
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                color="inherit"
                                sx={{
                                    mt: 3,
                                    backgroundColor: '#000',
                                    color: '#FFF',
                                    '&:hover': { backgroundColor: '#333' },
                                }}
                                disabled={isLoggingIn}
                            >
                                {isLoggingIn ? (
                                    <CircularProgress size={24} sx={{ color: '#FFF' }} />
                                ) : (
                                    'Login'
                                )}
                            </Button>

                        </Box>
                    </Card>
                </Box>
                <Snackbar
                    open={openSnackbar}
                    autoHideDuration={3000}
                    onClose={() => setOpenSnackbar(false)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Alert
                        onClose={() => setOpenSnackbar(false)}
                        severity={toastSeverity}
                        sx={{ width: '100%' }}
                    >
                        {toastMessage}
                    </Alert>
                </Snackbar>
            </Box>
        </AppTheme>
    );
}